import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../components/ui/ConfirmationDialog';
import { useSubmitSupportTicket } from '../lib/hooks/useApi';
import { Send, MessageSquare, CheckCircle, Sparkles, Mail, Clock, HelpCircle } from 'lucide-react';

const Support: React.FC = () => {
  const { user } = useAuth();
  const { confirm } = useConfirmation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedPriority, setSubmittedPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    category: 'general' as 'general' | 'technical' | 'account' | 'feature' | 'bug'
  });

  const submitSupportTicket = useSubmitSupportTicket({
    onSuccess: () => {
      // Store priority before resetting
      setSubmittedPriority(formData.priority);
      setFormData({
        subject: '',
        message: '',
        priority: 'medium',
        category: 'general'
      });
      setIsSuccess(true);
      // Auto-hide success message after 8 seconds
      setTimeout(() => setIsSuccess(false), 8000);
    },
    onError: (error: Error) => {
      console.error('Failed to submit support ticket:', error);
    }
  });

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const subjectOk = formData.subject.trim().length >= 1;
    const messageOk = formData.message.trim().length >= 5;
    if (!subjectOk || !messageOk) {
      confirm({
        title: 'Validation Error',
        description: 'Subject is required. Message must be at least 5 characters.',
        variant: 'warning',
        confirmText: 'OK',
        onConfirm: () => {}
      });
      return;
    }

    confirm({
      title: 'Submit Support Request',
      description: 'Are you sure you want to submit this support request? It will be sent to the admin team.',
      variant: 'info',
      confirmText: 'Submit',
      onConfirm: async () => {
        await submitSupportTicket.mutateAsync({
          subject: formData.subject,
          message: formData.message,
          priority: formData.priority,
          category: formData.category
        });
      }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background-enterprise-primary via-background-enterprise-secondary to-background-enterprise-tertiary">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto">
          {/* Success Message */}
          {isSuccess && (
            <div className="mb-6 animate-fade-in-up">
              <Card className="glass-card border-green-500/50 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 backdrop-blur-xl shadow-2xl">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                        <CheckCircle className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-text-enterprise-primary mb-2 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-green-500 animate-pulse" />
                        Ticket Successfully Submitted!
                      </h3>
                      <p className="text-text-enterprise-secondary mb-2">
                        Your support ticket has been sent to the admin team. You will receive a confirmation email shortly.
                      </p>
                      <div className="flex items-center gap-2 text-sm text-text-enterprise-tertiary mt-3">
                        <Mail className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span>We'll respond to <strong>{user?.email}</strong> within the estimated response time.</span>
                      </div>
                      <div className="mt-4 flex items-center gap-2 text-sm">
                        <Clock className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        <span className="text-text-enterprise-secondary">
                          Expected response: {submittedPriority === 'urgent' ? '2-4 hours' : 
                                           submittedPriority === 'high' ? '4-8 hours' :
                                           submittedPriority === 'medium' ? '1-2 days' : '2-3 days'}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => setIsSuccess(false)}
                      className="flex-shrink-0 text-text-enterprise-tertiary hover:text-text-enterprise-primary transition-colors"
                    >
                      <span className="sr-only">Close</span>
                      ✕
                    </button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Support Form */}
            <div className="lg:col-span-2">
              <Card className="glass-card backdrop-blur-xl bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50 shadow-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-text-enterprise-primary">
                    <MessageSquare className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Submit Support Request
                  </CardTitle>
                  <CardDescription className="text-text-enterprise-secondary">
                    Describe your issue or question in detail. Our admin team will get back to you via email.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* User Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-glass-surface/30 dark:bg-glass-surface-dark-mode/30 rounded-lg border border-glass-border/50 dark:border-glass-border-dark-mode/50">
                      <div>
                        <label className="block text-sm font-medium text-text-enterprise-primary mb-1">
                          Your Name
                        </label>
                        <Input
                          value={`${user?.first_name} ${user?.last_name}`}
                          disabled
                          className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-enterprise-primary mb-1">
                          Your Email
                        </label>
                        <Input
                          value={user?.email || ''}
                          disabled
                          className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50"
                        />
                      </div>
                    </div>

                    {/* Category and Priority */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                          Category
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => handleInputChange('category', e.target.value)}
                          className="w-full px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        >
                          <option value="general">General Question</option>
                          <option value="technical">Technical Issue</option>
                          <option value="account">Account Related</option>
                          <option value="feature">Feature Request</option>
                          <option value="bug">Bug Report</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                          Priority
                        </label>
                        <select
                          value={formData.priority}
                          onChange={(e) => handleInputChange('priority', e.target.value)}
                          className="w-full px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                        >
                          <option value="low">Low - General inquiry</option>
                          <option value="medium">Medium - Standard issue</option>
                          <option value="high">High - Important issue</option>
                          <option value="urgent">Urgent - Critical issue</option>
                        </select>
                      </div>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        placeholder="Brief description of your issue or question"
                        className="bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50 focus:ring-primary-500/50"
                        required
                        minLength={1}
                      />
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-sm font-medium text-text-enterprise-primary mb-2">
                        Message *
                      </label>
                      <div className="text-xs text-text-enterprise-tertiary mb-1">Minimum 5 characters</div>
                      <textarea
                        value={formData.message}
                        onChange={(e) => handleInputChange('message', e.target.value)}
                        placeholder="Please provide detailed information about your issue or question. Include any error messages, steps to reproduce, or specific requirements."
                        rows={8}
                        className="w-full px-3 py-2 bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border border-glass-border/50 dark:border-glass-border-dark-mode/50 rounded-lg text-text-enterprise-primary placeholder-text-enterprise-tertiary focus:outline-none focus:ring-2 focus:ring-primary-500/50 resize-vertical"
                        required
                        minLength={5}
                      />
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                      <Button
                        type="submit"
                        disabled={submitSupportTicket.isPending}
                        className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white px-8 py-2 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl"
                      >
                        {submitSupportTicket.isPending ? (
                          <>
                            <LoadingSpinner size="sm" variant="ai" className="mr-2" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Submit Support Request
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Support Info Sidebar */}
            <div className="space-y-6">
              {/* Response Time */}
              <Card className="glass-card backdrop-blur-xl bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50 shadow-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-text-enterprise-primary">
                    <Clock className="w-5 h-5 text-green-600 dark:text-green-400" />
                    Response Times
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center p-2 rounded-lg bg-red-500/10 dark:bg-red-500/20 border border-red-500/20 dark:border-red-500/30">
                    <span className="text-text-enterprise-primary font-medium">Urgent</span>
                    <span className="text-red-600 dark:text-red-400 font-semibold">2-4 hours</span>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded-lg bg-orange-500/10 dark:bg-orange-500/20 border border-orange-500/20 dark:border-orange-500/30">
                    <span className="text-text-enterprise-primary font-medium">High</span>
                    <span className="text-orange-600 dark:text-orange-400 font-semibold">4-8 hours</span>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 border border-blue-500/20 dark:border-blue-500/30">
                    <span className="text-text-enterprise-primary font-medium">Medium</span>
                    <span className="text-blue-600 dark:text-blue-400 font-semibold">1-2 days</span>
                  </div>
                  <div className="flex justify-between items-center p-2 rounded-lg bg-gray-500/10 dark:bg-gray-500/20 border border-gray-500/20 dark:border-gray-500/30">
                    <span className="text-text-enterprise-primary font-medium">Low</span>
                    <span className="text-gray-600 dark:text-gray-400 font-semibold">2-3 days</span>
                  </div>
                </CardContent>
              </Card>

              {/* Tips */}
              <Card className="glass-card backdrop-blur-xl bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50 shadow-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-text-enterprise-primary">
                    <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    Tips for Better Support
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-sm text-text-enterprise-secondary">
                    <p className="font-medium mb-3 text-text-enterprise-primary">To help us assist you better:</p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Be specific about the issue</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Include error messages if any</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Describe steps to reproduce</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Mention your browser/device</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                        <span>Attach screenshots if helpful</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Contact Info */}
              <Card className="glass-card backdrop-blur-xl bg-glass-surface/50 dark:bg-glass-surface-dark-mode/50 border-glass-border/50 dark:border-glass-border-dark-mode/50 shadow-2xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-text-enterprise-primary">
                    <Mail className="w-5 h-5 text-primary-600 dark:text-primary-400" />
                    Need Immediate Help?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-text-enterprise-secondary mb-3">
                    For urgent issues, you can also reach out to your team lead or admin directly.
                  </p>
                  <div className="p-3 bg-primary-500/10 dark:bg-primary-500/20 rounded-lg border border-primary-500/20 dark:border-primary-500/30">
                    <p className="text-xs text-text-enterprise-secondary">
                      <strong className="text-text-enterprise-primary">Note:</strong> Your support requests will be sent to the admin team via email at <strong className="text-primary-600 dark:text-primary-400">{user?.email}</strong>.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
